/*
 * -----------------------------------------------------------------------
 *  <copyright file="scatter-spline-chart.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */
import React, { useEffect, useRef, useState, useContext } from 'react';
import Highcharts from 'highcharts/esm/highcharts.js';
import 'highcharts/highcharts-more';
import HighchartsReact from 'highcharts-react-official';
import {
    FormatNumberForChart,
    HC_DIRECTION_NAMES,
    HC_LINE_STYLES,
    HC_RAG_NAMES,
    HC_COLORS_STANDARD,
    HC_COLORS_SAND_SIGNIKA,
    HC_TREND_LABELS,
    IOpraChart,
    IOpraChartSeries,
    OpraChartSeriesType,
    OpraColours,
    OpraVerticalAlign,
} from 'interfaces/opra-chart.interface';
import Loading from 'views/components/core/loading';
import { PanelFrameContext } from 'views/components/core/panel-frame';

const getStandardSeriesSpecificValues: any = (series: IOpraChartSeries) => {
    const highchartsMarkerShapes: string[] = ['circle', 'square', 'diamond', 'triangle', 'triangle-down'];
    const marker: any = {
        enabled:
            series.style !== undefined && series.style.marker !== undefined && series.style.marker.enabled !== false,
        symbol:
            series.style !== undefined && series.style?.marker !== undefined
                ? highchartsMarkerShapes[series.style?.marker.shape]
                : undefined,
        lineColor: series && !series.colorPerPoint && series.style !== undefined ? series.style.color : undefined,
        lineWidth: series?.style?.lineWidth ? series.style?.lineWidth : 1,
        radius: series?.style?.marker && series.style?.marker.size ? series.style?.marker.size : 2,
        fillColor:
            series && !series.colorPerPoint && series.style && series.style?.marker && series.style?.marker.isHollow
                ? { pattern: { opacity: 0 } }
                : undefined,
    };

    switch (series.type) {
        case OpraChartSeriesType.SmoothLine:
            return {
                type: 'spline',
                marker,
                lineWidth: series?.style?.lineWidth ? series.style?.lineWidth : 1,
                dashStyle: series?.style === undefined ? HC_LINE_STYLES[0] : HC_LINE_STYLES[series.style.lineStyle],
            };
        case OpraChartSeriesType.Line:
            return {
                type: 'line',
                marker,
                lineWidth: series?.style?.lineWidth ? series.style?.lineWidth : 1,
                dashStyle: series?.style === undefined ? HC_LINE_STYLES[0] : HC_LINE_STYLES[series.style.lineStyle],
            };
        default:
            return {
                type: 'scatter',
                marker,
            };
    }
};

const ScatterLineChart = (chartProps: IOpraChart) => {
    const {
        yAxisTitle,
        plotTitle,
        series,
        horizontalLines,
        xAxis,
        secondaryKey,
        showLegend,
        verticalLines,
        isCustomLegendWidth,
        getRef,
        tooltipShowSeriesLabel,
        tooltipShowSeriesThreshold,
        minY,
        maxY,
        hidePlotTitle,
    } = chartProps;
    const [options, setOptions] = useState<HighchartsReact.Props | null>(null);
    const charts = useRef<HighchartsReact.RefObject>(null);
    const panelFrameContext = useContext(PanelFrameContext);
    const [style, setStyle] = useState({
        style: {
            height: '350px',
        },
    });
    const turboThreshold: number = 2000;

    const seriesThreshold = ['None', 'Severe', 'Moderate', 'Normal'];
    useEffect(() => {
        if (getRef && charts) getRef(charts.current);
    }, [charts.current, options]);

    useEffect(() => {
        if (panelFrameContext?.height && panelFrameContext?.height !== style.style.height)
            setStyle({
                style: {
                    height: panelFrameContext?.height,
                },
            });
    }, [panelFrameContext?.height]);

    const handleMin = () => {
        const array: number[] = [];
        for (let i = 0; i < series.length; i++) {
            for (let j = 0; j < (series[i] as any).data.length; j++) {
                array.push(Number((series[i].data[j] as any).x));
            }
        }
        return array.sort((a, b) => a - b)[0];
    };

    const getSeries = () => {
        const hasSelection = series.filter((s) => s && s.data.filter((d) => d.selected).length > 0).length > 0;
        const array = [];
        for (let i = 0; i < series.length; i++) {
            if (!series[i]) return;
            const result = [
                {
                    name: series[i].name,
                    color: series[i].style ? series[i]?.style?.color : undefined,
                    ...getStandardSeriesSpecificValues(series[i]),
                    data:
                        turboThreshold > 0 && series[i]?.data.length <= turboThreshold
                            ? series[i]?.data.map((d) => {
                                  const baseColour =
                                      series[i].colorPerPoint && d.rag
                                          ? HC_COLORS_STANDARD[d.rag]
                                          : series[i].style?.color;

                                  return {
                                      ...d,
                                      color: series[i].colorPerPoint && d.rag ? HC_COLORS_STANDARD[d.rag] : undefined,
                                      lineColor:
                                          series[i].colorPerPoint && d.rag ? HC_COLORS_STANDARD[d.rag] : undefined,
                                      marker:
                                          hasSelection && series[i].type === OpraChartSeriesType.Scatter
                                              ? {
                                                    radius: d.selected ? 7 : undefined,
                                                    lineColor: baseColour
                                                        ? `${baseColour.substr(0, 7)}${d.selected ? '80' : '99'}`
                                                        : undefined,
                                                    fillColor: d.selected
                                                        ? undefined
                                                        : baseColour
                                                          ? `${baseColour.substr(0, 7)}99`
                                                          : undefined,
                                                    lineWidth: d.selected ? 7 : undefined,
                                                }
                                              : undefined,
                                      selected: false,
                                  };
                              })
                            : series[i]?.data.map((d) => [d.x, d.y]),
                    showInLegend: series[i].showInLegend,
                    tooltip: {
                        enabled: series[i].showInTooltip === undefined || (series[i].showInTooltip ?? true),
                    },
                    states: {
                        hover: {
                            enabled: series[i].showInTooltip === undefined || (series[i].showInTooltip ?? true),
                        },
                    },
                },
            ];
            if (series[i].hasConfidences) {
                result.push({
                    name: `${series[i].name} Confidence Interval`,
                    color: series[i].style ? series[i].style?.color : undefined,
                    tooltip: {
                        enabled: series[i].showInTooltip === undefined || (series[i].showInTooltip ?? true),
                    },
                    states: {
                        hover: {
                            enabled: series[i].showInTooltip === undefined || (series[i].showInTooltip ?? true),
                        },
                        select: {
                            enabled: series[i].showInTooltip === undefined || (series[i].showInTooltip ?? true),
                        },
                    },
                    type: series[i].type === OpraChartSeriesType.SmoothLine ? 'areasplinerange' : 'arearange',
                    data: series[i]?.data.map((d) => [d.x, d.confidence.yMin, d.confidence.yMax]),
                });
            }
            array.push(...result);
        }

        return array;
    };

    const calcJitter = (pct: number) => {
        const allXPoints = series?.flatMap((x) => x?.data?.map((xx) => xx.x)) as number[];
        const minVal = Math.min(...allXPoints);
        const maxVal = Math.max(...allXPoints);
        const result = (maxVal - minVal) * (pct / 100);
        return result;
    };

    useEffect(() => {
        if (!series?.filter || typeof series.filter !== 'function') return;
        setOptions({
            canRender:
                series && series.filter((s) => s && s.colorPerPoint && s.data.length > turboThreshold).length === 0,
            hcConfig: {
                updateArgs: [true, false, false],
                colors: HC_COLORS_SAND_SIGNIKA,
                credits: {
                    enabled: false,
                },
                chart: {
                    zoomType: 'xy',
                },
                exporting: {
                    enabled: !getRef,
                    fallbackToExportServer: false,
                    chartOptions: { title: { text: plotTitle, style: { fontWeight: 'semi-bold' } } },
                    buttons: {
                        contextButton: {
                            enabled: false,
                        },
                    },
                },
                title: {
                    text: hidePlotTitle ? null : plotTitle,
                },
                subtitle:
                    secondaryKey === undefined
                        ? undefined
                        : {
                              text: secondaryKey.text,
                              useHTML: true,
                              verticalAlign:
                                  secondaryKey.position === OpraVerticalAlign.top
                                      ? 'top'
                                      : secondaryKey.position === OpraVerticalAlign.middle
                                        ? 'middle'
                                        : 'bottom',
                              style: { display: (showLegend ?? true) ? undefined : 'none' },
                          },
                legend: {
                    enabled: showLegend,
                    itemWidth: isCustomLegendWidth ? 150 : undefined,
                    width: isCustomLegendWidth ? 450 : undefined,
                    itemStyle: {
                        color: HC_COLORS_STANDARD[OpraColours.DarkGrey],
                        cursor: 'pointer',
                        fontWeight: 'normal',
                        fontSize: '12px',
                        textOverflow: 'ellipsis',
                    },
                },
                xAxis: {
                    type: xAxis.isDateRange ? 'datetime' : 'linear',
                    title: {
                        text: xAxis.title,
                    },
                    min: !xAxis.isDateRange ? undefined : handleMin(),
                    crosshair: true,
                    plotLines:
                        verticalLines === undefined
                            ? undefined
                            : verticalLines.map((pl) => ({
                                  color: pl.color,
                                  dashStyle: HC_LINE_STYLES[pl.style],
                                  label: { text: pl.name },
                                  value: pl.x,
                                  zIndex: 1,
                              })),
                },
                tooltip: {
                    formatter() {
                        const self: any = this;
                        if (
                            !self.point?.series?.options?.tooltip?.enabled ||
                            (self.point?.options?.hideTooltip ?? false)
                        )
                            return false;
                        const threshold = self.point.rag !== undefined ? `${seriesThreshold[self.point.rag]}: ` : '';
                        let result = tooltipShowSeriesThreshold ? `<b>${threshold}</b>` : '';
                        if (tooltipShowSeriesLabel) result += `<b>${self.series.name}</b>`;
                        if (self.point.site !== undefined) result += `<br/>Site: ${self.point.site}`;
                        if (self.point.rag !== undefined)
                            result += `<br/>RAG: ${
                                self.point.direction !== undefined && self.point.direction !== null
                                    ? HC_DIRECTION_NAMES[self.point.direction]
                                    : ''
                            } ${HC_RAG_NAMES[self.point.rag]}`;
                        result += '<br/>X-Axis: ';
                        if (self.series.xAxis.userOptions.type === 'datetime') {
                            result += new Date(self.point.x).toDateString();
                        } else {
                            result += `${FormatNumberForChart(self.point.x)}`;
                        }
                        result += `<br/>Y-Axis: ${FormatNumberForChart(self.point.y)}`;
                        if (self.point.trend !== undefined)
                            result += `<br/>Trend: ${HC_TREND_LABELS[self.point.trend]}`;
                        if (self.point.score !== undefined)
                            result += `<br/>Score: ${FormatNumberForChart(self.point.score)}`;
                        if (self.point.pValue !== undefined)
                            result += `<br/>PValue: ${FormatNumberForChart(self.point.pValue)}`;
                        return result;
                    },
                },
                yAxis: {
                    title: {
                        text: yAxisTitle,
                    },
                    min: minY,
                    max: maxY,
                    crosshair: true,
                    plotLines:
                        horizontalLines === undefined
                            ? undefined
                            : horizontalLines.map((pl) => ({
                                  color: pl.color,
                                  dashStyle: HC_LINE_STYLES[pl.style],
                                  label: { text: pl.name },
                                  value: pl.y,
                                  zIndex: 1,
                              })),
                },
                plotOptions: {
                    series: {
                        cursor: 'pointer',
                        turboThreshold,
                        stickyTracking: false,
                    },
                    areasplinerange: {
                        enableMouseTracking: false,
                        zIndex: 0,
                        lineWidth: 0,
                        marker: { enabled: false },
                        allowPointSelect: false,
                        fillOpacity: 0.2,
                        showInLegend: false,
                        trackByArea: false,
                        linkedTo: ':previous',
                    },
                    arearange: {
                        enableMouseTracking: false,
                        zIndex: 0,
                        lineWidth: 0,
                        marker: { enabled: false },
                        allowPointSelect: false,
                        fillOpacity: 0.1,
                        showInLegend: false,
                        trackByArea: false,
                        linkedTo: ':previous',
                    },
                    spline: {
                        zIndex: 2,
                    },
                    line: {
                        zIndex: 3,
                    },
                    scatter: {
                        zIndex: 4,
                    },
                },
                series: getSeries(),
            },
        });
    }, [chartProps]);

    if (!options) return <Loading />;

    if (!options.canRender)
        return <div>Unable to render chart due to more than {turboThreshold} data points in 1 or more a series</div>;

    return (
        <div>
            <HighchartsReact
                ref={charts}
                highcharts={Highcharts}
                constructorType="chart"
                options={options.hcConfig}
                containerProps={style}
                // Mending size after fullscreen and setting with for expand chart functionality
            />
        </div>
    );
};

export default ScatterLineChart;
